import { useEffect, useRef, useState, useId } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import StudioIcon from './StudioIcon';

export default function AIRewrite({ value, onApply, field = 'brief', mediaType = 'text', disabled = false }) {
  const { t } = useI18n();
  const id = useId();
  const [mode, setMode] = useState('polish');
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState('');
  const controller = useRef(null);
  const requestId = useRef(0);
  const text = String(value || '');

  useEffect(() => {
    requestId.current += 1;
    controller.current?.abort();
    setBusy(false);
    setSuggestion(null);
    setError('');
    return () => controller.current?.abort();
  }, [text, field, mediaType]);

  async function rewrite() {
    if (busy || disabled || !text.trim() || text.length > 6000) return;
    const currentRequest = ++requestId.current;
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    setBusy(true);
    setError('');
    setSuggestion(null);
    try {
      const response = await fetch('/api/rewrite', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ text, mode, field, mediaType }), signal: abort.signal,
      });
      const result = await response.json();
      if (!response.ok || !result.text?.trim()) throw new Error('rewrite_failed');
      if (currentRequest === requestId.current && !abort.signal.aborted) setSuggestion(result.text);
    } catch (err) {
      if (err.name !== 'AbortError' && currentRequest === requestId.current) setError(t('rewrite.error'));
    } finally {
      if (currentRequest === requestId.current) setBusy(false);
    }
  }

  return <div className="AIRewrite">
    <div className="AIRewriteToolbar">
      <button type="button" className="AIRewriteTrigger" onClick={rewrite} disabled={disabled || busy || !text.trim() || text.length > 6000}>
        <StudioIcon size={14} /> {busy ? t('rewrite.busy') : t('rewrite.action')}
      </button>
      <select aria-label={t('rewrite.style')} value={mode} onChange={(e) => { setMode(e.target.value); setSuggestion(null); }} disabled={busy || disabled}>
        {['polish', 'shorten', 'expand', 'professional'].map((option) => <option key={option} value={option}>{t(`rewrite.${option}`)}</option>)}
      </select>
    </div>
    {busy ? <p className="AIRewriteStatus" role="status">{t('rewrite.working')}</p> : null}
    {error ? <p className="AIRewriteError" role="alert">{error}</p> : null}
    {suggestion !== null ? <div className="AIRewriteReview">
      <label htmlFor={id}>{t('rewrite.suggestion')}</label>
      <textarea id={id} value={suggestion} onChange={(e) => setSuggestion(e.target.value)} rows={4} dir="auto" />
      <p>{t('rewrite.review')}</p>
      <div>
        <button type="button" className="Btn Btn--ghost" onClick={() => setSuggestion(null)}>{t('rewrite.discard')}</button>
        <button type="button" className="Btn" disabled={disabled || !suggestion.trim()} onClick={() => { onApply(suggestion.trim()); setSuggestion(null); }}>{t('rewrite.apply')}</button>
      </div>
    </div> : null}
  </div>;
}
